/**
 * Appointment reason is collected on service detail (or lab selection) and
 * must only be sent for that same scheduling flow. It lives in sessionStorage
 * so a closed tab cannot reuse it; leftover localStorage keys are still read
 * once, then removed.
 */
export const REASON_TEXT_KEY = 'reason'
export const REASONS_FOR_APPOINTMENT_KEY = 'reasonsForAppointment'
export const REASON_SERVICE_ID_KEY = 'appointmentReasonServiceId'

const normalizeServiceId = (serviceId) => {
  if (serviceId == null || serviceId === '') return null
  return String(serviceId)
}

const parseJson = (raw) => {
  if (raw == null || raw === '') return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const safeGetItem = (key) => {
  try {
    return (
      window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key)
    )
  } catch {
    return null
  }
}

const safeSetItem = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value)
    window.localStorage.removeItem(key)
  } catch {
    // ignore quota errors
  }
}

const safeRemoveItem = (key) => {
  try {
    window.sessionStorage.removeItem(key)
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export const getStoredAppointmentReasonServiceId = () =>
  normalizeServiceId(safeGetItem(REASON_SERVICE_ID_KEY))

export const isAppointmentReasonForService = (serviceId) => {
  const stored = getStoredAppointmentReasonServiceId()
  const current = normalizeServiceId(serviceId)
  return Boolean(stored && current && stored === current)
}

export const clearAppointmentReasonStorage = () => {
  safeRemoveItem(REASON_TEXT_KEY)
  safeRemoveItem(REASONS_FOR_APPOINTMENT_KEY)
  safeRemoveItem(REASON_SERVICE_ID_KEY)
}

const persistReasonServiceId = (serviceId) => {
  const id = normalizeServiceId(serviceId)
  if (!id) return
  safeSetItem(REASON_SERVICE_ID_KEY, id)
}

export const persistTextReason = (serviceId, text) => {
  const id = normalizeServiceId(serviceId)
  const trimmed = typeof text === 'string' ? text.trim() : ''
  if (!id || !trimmed) {
    safeRemoveItem(REASON_TEXT_KEY)
    return
  }
  safeRemoveItem(REASONS_FOR_APPOINTMENT_KEY)
  safeSetItem(REASON_TEXT_KEY, JSON.stringify(trimmed))
  persistReasonServiceId(id)
}

export const persistReasonsForAppointment = (serviceId, reasons) => {
  const id = normalizeServiceId(serviceId)
  const list = Array.isArray(reasons) ? reasons : []
  if (!id || list.length === 0) {
    safeRemoveItem(REASONS_FOR_APPOINTMENT_KEY)
    return
  }
  safeRemoveItem(REASON_TEXT_KEY)
  safeSetItem(REASONS_FOR_APPOINTMENT_KEY, JSON.stringify(list))
  persistReasonServiceId(id)
}

export const readTextReasonForService = (serviceId) => {
  if (!isAppointmentReasonForService(serviceId)) return ''
  const value = parseJson(safeGetItem(REASON_TEXT_KEY))
  return typeof value === 'string' ? value : ''
}

export const readReasonsForAppointmentForService = (serviceId) => {
  if (!isAppointmentReasonForService(serviceId)) return undefined
  const value = parseJson(safeGetItem(REASONS_FOR_APPOINTMENT_KEY))
  return Array.isArray(value) ? value : undefined
}

const nonEmptyStrings = (values) =>
  values.filter((item) => typeof item === 'string' && item.trim() !== '')

/**
 * Reason is only filled when this flow collected it (service detail / lab
 * selection). The backend still expects the field, so unrelated or empty
 * values become [].
 */
export const resolveScheduleReason = ({
  isLaboratories = false,
  laboratoriesLabels = [],
  reasonsForAppointment,
  reason
} = {}) => {
  if (isLaboratories) {
    return nonEmptyStrings(
      Array.isArray(laboratoriesLabels) ? laboratoriesLabels : []
    )
  }

  if (Array.isArray(reasonsForAppointment) && reasonsForAppointment.length) {
    return nonEmptyStrings(
      reasonsForAppointment.map((item) =>
        typeof item === 'string' ? item : item?.value
      )
    )
  }

  if (typeof reason === 'string' && reason.trim()) {
    return [reason.trim()]
  }

  return []
}

export const withScheduleReason = (body, reason) => ({
  ...body,
  reason: Array.isArray(reason) ? nonEmptyStrings(reason) : []
})
